import React, { useState, useEffect } from 'react';
import {
  Pane,
  Card,
  Paragraph,
  Button,
  Heading
} from 'evergreen-ui';
import ContactFormModal from './ContactFormModal';

/**
 * A component that displays a friendly help message with a clickable button to open a contact form
 *
 * @param {number} delay - Delay in milliseconds before showing the help box (default: 3000ms)
 * @param {string} message - The help message to display
 * @param {string} linkText - The text for the clickable button
 * @returns {React.Component}
 */
const HelpBox = ({
  delay = 3000,
  message = "We're here to answer questions and lend a hand.",
  linkText = "Drop us a line"
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Show the help box after the specified delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    // Clean up the timer when the component unmounts
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <>
      <style>
        {`
          .help-button {
            cursor: pointer !important;
            text-decoration: underline !important;
            color: var(--maroon) !important;
            font-weight: 500 !important;
          }
          .help-button:hover {
            background-color: rgba(255, 255, 255, 0.5) !important;
          }
          .slide-in-container {
            position: relative;
            width: 200px;
            height: 200px;
            margin-top: 16px;
            overflow: hidden;
          }
          .slide-in-box {
            position: absolute;
            top: 0;
            right: 0;
            width: 100%;
            height: 100%;
            transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1);
            transform: translateX(${isVisible ? '0' : '100%'});
            opacity: ${isVisible ? '1' : '0'};
          }
        `}
      </style>
      <div className="slide-in-container">
        <div className="slide-in-box">
          <Card
            elevation={0}
            backgroundColor="var(--lightgreen)"
            border="1px solid var(--medgreen)"
            padding={16}
            borderRadius={8}
            width="100%"
            display="flex"
            flexDirection="column"
          >
            <Heading size={500} marginBottom={12} color="var(--darkgreen)">
              Need help uploading?
            </Heading>
            <Paragraph marginBottom={12}>
              {message}
            </Paragraph>
            <Button
              appearance="minimal"
              intent="none"
              height={32}
              marginTop={4}
              onClick={() => setShowModal(true)}
              className="help-button"
            >
              {linkText}
            </Button>
          </Card>
        </div>
      </div>

      <ContactFormModal
        isShown={showModal}
        onClose={() => setShowModal(false)}
        title="Contact Support"
        description="Have questions about importing orders? We're ready to help!"
      />
    </>
  );
};

export default HelpBox;
